"use client";

/**
 * ConfirmActionModal — utilitarian confirmation for Run now, Fork, Pause/Resume.
 * Uses IcyDialogShell with per-action accent tones (sky / amber / emerald).
 */

import type { LucideIcon } from "lucide-react";
import { SUB_CLASS, type AlertTone } from "@/lib/alert-theme";
import {
  IcyDialogShell,
  PrimaryDialogButton,
  SecondaryDialogButton,
} from "./edit-modals/IcyDialogShell";

export type ConfirmAccent = "icy" | "amber" | "emerald" | "slate";

const ACCENT_TO_TONE: Record<ConfirmAccent, AlertTone> = {
  icy: "icy",
  amber: "amber",
  emerald: "emerald",
  slate: "slate",
};

const ACCENT_TO_BUTTON: Record<
  ConfirmAccent,
  "icy" | "pearl" | "amber" | "emerald"
> = {
  icy: "icy",
  amber: "amber",
  emerald: "emerald",
  slate: "pearl",
};

interface ConfirmActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  busy?: boolean;
  title: string;
  subtitle?: string;
  description?: React.ReactNode;
  confirmLabel: string;
  Icon: LucideIcon;
  accent?: ConfirmAccent;
}

export function ConfirmActionModal({
  open,
  onOpenChange,
  onConfirm,
  busy = false,
  title,
  subtitle,
  description,
  confirmLabel,
  Icon,
  accent = "icy",
}: ConfirmActionModalProps) {
  return (
    <IcyDialogShell
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
      title={title}
      subtitle={subtitle}
      Icon={Icon}
      tone={ACCENT_TO_TONE[accent]}
      compact
      footer={
        <>
          <SecondaryDialogButton
            onClick={() => onOpenChange(false)}
            disabled={busy}
            size="sm"
          >
            Cancel
          </SecondaryDialogButton>
          <PrimaryDialogButton
            onClick={onConfirm}
            busy={busy}
            size="sm"
            variant={ACCENT_TO_BUTTON[accent]}
          >
            {confirmLabel}
          </PrimaryDialogButton>
        </>
      }
    >
      {description ? (
        <p className={`${SUB_CLASS} leading-relaxed`}>{description}</p>
      ) : null}
    </IcyDialogShell>
  );
}
